/* eslint-disable @typescript-eslint/naming-convention */

import type { OfferDefinition } from '../../libs/offerSystem/offerSystemTypes';
import type { Condition } from './conditionsTypes';
import { ConditionType } from './conditionsTypes';
import type { OffersGameData } from './offersTypes';
import { OfferLocation, OfferTag, OfferTrigger } from './offersTypes';
import { IAPProductId } from '../iap/types';
import { InventoryItem } from '../resources/types';
import { PiggyBankTier } from '../../gameEvents/piggyBank/types';
import { PIGGY_BANK_TIER_CONFIG_MAP } from '../../gameEvents/piggyBank/PiggyBankConstants';

export const offersConfig: OfferDefinition<Condition, OffersGameData>[] = [
  {
    id: IAPProductId.CoinPack01,
    displayName: null,
    location: [OfferLocation.Shop],
    triggers: [],
    data: {},
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    tags: ['BasicGold'],
    priority: 10,
    variants: [
      {
        id: 'Default',
        data: {
          offerSimpleView: {
            icon: { texture: 'Coins_1' },
          },
        },
        badge: null,
        iapCostCents: 199,
        content: {
          [InventoryItem.Coin]: 1200,
        },
        platformId: 'coin_pack_1',
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.PiggyBank,
    displayName: null,
    location: [],
    triggers: [],
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    data: {},
    tags: ['PiggyBank'],
    priority: 0,
    variants: [
      {
        id: 'Default',
        data: null,
        badge: null,
        iapCostCents: 399,
        content: {
          [InventoryItem.Coin]: PIGGY_BANK_TIER_CONFIG_MAP[PiggyBankTier.Normal].maxCoins,
        },
        platformId: 'piggy_bank',
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.PiggyBankSilver,
    displayName: null,
    location: [],
    triggers: [],
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    data: {},
    tags: ['PiggyBank'],
    priority: 0,
    variants: [
      {
        id: 'Default',
        data: null,
        badge: null,
        iapCostCents: 699,
        content: {
          [InventoryItem.Coin]: PIGGY_BANK_TIER_CONFIG_MAP[PiggyBankTier.Silver].maxCoins,
        },
        platformId: 'piggy_bank_t2',
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.PiggyBankGold,
    displayName: null,
    location: [],
    triggers: [],
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    data: {},
    tags: ['PiggyBank'],
    priority: 0,
    variants: [
      {
        id: 'Default',
        data: null,
        badge: null,
        iapCostCents: 1299,
        content: {
          [InventoryItem.Coin]: PIGGY_BANK_TIER_CONFIG_MAP[PiggyBankTier.Gold].maxCoins,
        },
        platformId: 'piggy_bank_t3',
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.PiggyBankOpal,
    displayName: null,
    location: [],
    triggers: [],
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    data: {},
    tags: ['PiggyBank'],
    priority: 0,
    variants: [
      {
        id: 'Default',
        data: null,
        badge: null,
        iapCostCents: 2499,
        content: {
          [InventoryItem.Coin]: PIGGY_BANK_TIER_CONFIG_MAP[PiggyBankTier.Opal].maxCoins,
        },
        platformId: 'piggy_bank_t4',
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.PiggyBankDiamond,
    displayName: null,
    location: [],
    triggers: [],
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    data: {},
    tags: ['PiggyBank'],
    priority: 0,
    variants: [
      {
        id: 'Default',
        data: null,
        badge: null,
        iapCostCents: 4999,
        content: {
          [InventoryItem.Coin]: PIGGY_BANK_TIER_CONFIG_MAP[PiggyBankTier.Diamond].maxCoins,
        },
        platformId: 'piggy_bank_t5',
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.SeasonPass,
    displayName: null,
    location: [OfferLocation.Shop, OfferLocation.LevelFailBanner],
    triggers: [],
    startConditions: {
      groupType: 'AND',
      conditions: [
        {
          type: ConditionType.ActiveSeasonPassWithoutPremium,
          value: true,
        },
      ],
    },
    endConditions: {
      groupType: 'OR',
      conditions: [
        {
          type: ConditionType.ActiveSeasonPassWithoutPremium,
          value: false,
        },
        {
          type: ConditionType.BoughtInstanceLimit,
          operator: 'GREATER OR EQUAL',
          value: 1,
        },
      ],
    },
    timeLimit: {},
    data: {
      shopOfferView: {
        layoutPath: 'seasonPass/SeasonPassShopOfferLayout',
      },
    },
    tags: [OfferTag.EventPass],
    priority: 10000,
    variants: [
      {
        id: 'pp1',
        data: null,
        badge: null,
        iapCostCents: 799,
        content: {},
        platformId: 'premium_pass_pp1',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.Tier1Country,
              value: false,
            },
            {
              groupType: 'OR',
              conditions: [
                {
                  type: ConditionType.HS774Distribution,
                  distribution: 2,
                },
                {
                  type: ConditionType.HS774Distribution,
                  distribution: 4,
                },
              ],
            },
          ],
        },
      },
      {
        id: 'pp2',
        data: null,
        badge: null,
        iapCostCents: 699,
        content: {},
        platformId: 'premium_pass_pp2',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.Tier1Country,
              value: true,
            },
            {
              groupType: 'OR',
              conditions: [
                {
                  type: ConditionType.HS774Distribution,
                  distribution: 3,
                },
                {
                  type: ConditionType.HS774Distribution,
                  distribution: 5,
                },
              ],
            },
          ],
        },
      },
      {
        id: 'pp3',
        data: null,
        badge: null,
        iapCostCents: 499,
        content: {},
        platformId: 'premium_pass_pp3',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.Tier1Country,
              value: false,
            },
            {
              groupType: 'OR',
              conditions: [
                {
                  type: ConditionType.HS774Distribution,
                  distribution: 3,
                },
                {
                  type: ConditionType.HS774Distribution,
                  distribution: 5,
                },
              ],
            },
          ],
        },
      },
      {
        id: 'default',
        data: null,
        badge: null,
        iapCostCents: 999,
        content: {},
        platformId: 'premium_pass',
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.CoinPack02,
    displayName: null,
    location: [OfferLocation.Shop],
    triggers: [],
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    data: {},
    tags: ['BasicGold'],
    priority: 9,
    variants: [
      {
        id: 'Default',
        data: {
          offerSimpleView: {
            icon: { texture: 'Coins_2' },
          },
        },
        badge: null,
        iapCostCents: 899,
        content: {
          [InventoryItem.Coin]: 6000,
        },
        platformId: 'coin_pack_2',
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.CoinPack03,
    displayName: null,
    location: [OfferLocation.Shop],
    triggers: [],
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    tags: ['BasicGold'],
    priority: 8,
    data: {},
    variants: [
      {
        id: 'Default',
        data: {
          offerSimpleView: {
            icon: { texture: 'Coins_3' },
          },
        },
        badge: null,
        iapCostCents: 1799,
        content: {
          [InventoryItem.Coin]: 12000,
        },
        platformId: 'coin_pack_3',
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.CoinPack04,
    displayName: null,
    location: [OfferLocation.Shop],
    triggers: [],
    startConditions: null,
    endConditions: null,
    data: {},
    timeLimit: {},
    tags: ['BasicGold'],
    priority: 7,
    variants: [
      {
        id: 'Default',
        data: {
          offerSimpleView: {
            icon: { texture: 'Coins_4' },
          },
        },
        badge: null,
        iapCostCents: 3499,
        content: {
          [InventoryItem.Coin]: 30000,
        },
        platformId: 'coin_pack_4',
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.CoinPack05,
    displayName: null,
    location: [OfferLocation.Shop],
    triggers: [],
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    tags: ['BasicGold'],
    priority: 6,
    data: {},
    variants: [
      {
        id: 'Default',
        data: {
          offerSimpleView: {
            icon: { texture: 'Coins_5' },
          },
        },
        badge: null,
        iapCostCents: 5999,
        content: {
          [InventoryItem.Coin]: 60000,
        },
        platformId: 'coin_pack_5',
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.CoinPack06,
    displayName: null,
    location: [OfferLocation.Shop],
    triggers: [],
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    tags: ['BasicGold'],
    priority: 5,
    data: {},
    variants: [
      {
        id: 'Default',
        badge: null,
        data: {
          offerSimpleView: {
            icon: { texture: 'Coins_6' },
          },
        },
        iapCostCents: 9999,
        content: {
          [InventoryItem.Coin]: 120000,
        },
        platformId: 'coin_pack_6',
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.BasicBundle01,
    displayName: {
      en_US: 'Apprentice Bundle',
      it_IT: 'Pacchetto Apprendista',
      fr_FR: 'Pack Apprenti',
      de_DE: 'Lehrlingspaket',
      es_ES: 'Paquete Aprendiz',
      pt_PT: 'Pacote Aprendiz',
      pl_PL: 'Pakiet Ucznia',
      nl_NL: 'Leerlingbundel',
      ko_KR: '초보자 번들',
      ja_JP: '初心者バンドル',
    },
    location: [OfferLocation.Shop, OfferLocation.LevelFailBanner],
    triggers: [],
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    tags: ['BasicBundle'],
    priority: 110,
    data: {},
    variants: [
      {
        id: 'Default',
        badge: null,
        data: {},
        platformId: 'basic_bundle_1',
        iapCostCents: 999,
        content: {
          [InventoryItem.Coin]: 7000,
          [InventoryItem.BoosterMove]: 1,
          [InventoryItem.BoosterRedraw]: 1,
          [InventoryItem.BoosterHammer]: 1,
          [InventoryItem.UnlimitedLivesMinutes]: 60,
        },
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.BasicBundle02,
    displayName: {
      en_US: 'Skilled Bundle',
      it_IT: 'Pacchetto Abile',
      fr_FR: 'Pack Compétent',
      de_DE: 'Fähigen-Paket',
      es_ES: 'Paquete Hábil',
      pt_PT: 'Pacote Hábil',
      pl_PL: 'Pakiet Zręczności',
      nl_NL: 'Vaardige Bundel',
      ko_KR: '숙련 번들',
      ja_JP: 'スキルバンドル',
    },
    location: [OfferLocation.Shop, OfferLocation.LevelFailBanner],
    triggers: [],
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    tags: ['BasicBundle'],
    priority: 109,
    data: {},
    variants: [
      {
        id: 'Default',
        badge: null,
        platformId: 'basic_bundle_2',
        iapCostCents: 1999,
        data: {},
        content: {
          [InventoryItem.Coin]: 14000,
          [InventoryItem.BoosterMove]: 2,
          [InventoryItem.BoosterRedraw]: 2,
          [InventoryItem.BoosterHammer]: 2,
          [InventoryItem.UnlimitedLivesMinutes]: 300,
        },
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.BasicBundle03,
    displayName: {
      en_US: 'Advanced Bundle',
      it_IT: 'Pacchetto Avanzato',
      fr_FR: 'Pack Avancé',
      de_DE: 'Erweitertes Paket',
      es_ES: 'Paquete Avanzado',
      pt_PT: 'Pacote Avançado',
      pl_PL: 'Zaawansowany Pakiet',
      nl_NL: 'Geavanceerde Bundel',
      ko_KR: '고급 번들',
      ja_JP: 'アドバンスバンドル',
    },
    location: [OfferLocation.Shop, OfferLocation.LevelFailBanner],
    triggers: [],
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    tags: ['BasicBundle'],
    priority: 108,
    data: {},
    variants: [
      {
        id: 'Default',
        data: {},
        badge: {
          en_US: 'POPULAR',
          it_IT: 'POPOLARE',
          fr_FR: 'POPULAIRE',
          de_DE: 'BELIEBT',
          es_ES: 'POPULAR',
          pt_PT: 'POPULAR',
          pl_PL: 'POPULARNE',
          nl_NL: 'POPULAIR',
          ko_KR: '인기',
          ja_JP: '人気',
        },
        platformId: 'basic_bundle_3',
        iapCostCents: 3999,
        content: {
          [InventoryItem.Coin]: 40000,
          [InventoryItem.BoosterMove]: 4,
          [InventoryItem.BoosterRedraw]: 4,
          [InventoryItem.BoosterHammer]: 4,
          [InventoryItem.UnlimitedLivesMinutes]: 600,
        },
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.BasicBundle04,
    displayName: {
      en_US: 'Expert Bundle',
      it_IT: 'Pacchetto Esperto',
      fr_FR: 'Pack Expert',
      de_DE: 'Experten-Paket',
      es_ES: 'Paquete Experto',
      pt_PT: 'Pacote Especialista',
      pl_PL: 'Pakiet Eksperta',
      nl_NL: 'Expert Bundel',
      ko_KR: '전문가 번들',
      ja_JP: 'エキスパートバンドル',
    },
    location: [OfferLocation.Shop, OfferLocation.LevelFailBanner],
    triggers: [],
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    tags: ['BasicBundle'],
    priority: 107,
    data: {},
    variants: [
      {
        id: 'Default',
        badge: null,
        data: {},
        platformId: 'basic_bundle_4',
        iapCostCents: 6999,
        content: {
          [InventoryItem.Coin]: 70000,
          [InventoryItem.BoosterMove]: 7,
          [InventoryItem.BoosterRedraw]: 7,
          [InventoryItem.BoosterHammer]: 7,
          [InventoryItem.UnlimitedLivesMinutes]: 900,
        },
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.BasicBundle05,
    displayName: {
      en_US: 'Master Bundle',
      it_IT: 'Pacchetto Maestro',
      fr_FR: 'Pack Maître',
      de_DE: 'Meister-Paket',
      es_ES: 'Paquete Maestro',
      pt_PT: 'Pacote Mestre',
      pl_PL: 'Pakiet Mistrza',
      nl_NL: 'Meester Bundel',
      ko_KR: '마스터 번들',
      ja_JP: 'マスターバンドル',
    },
    location: [OfferLocation.Shop, OfferLocation.LevelFailBanner],
    triggers: [],
    startConditions: null,
    endConditions: null,
    timeLimit: {},
    tags: ['BasicBundle'],
    priority: 106,
    data: {},
    variants: [
      {
        id: 'Default',
        badge: {
          en_US: 'BEST VALUE',
          it_IT: 'MIGLIOR OFFERTA',
          fr_FR: 'MEILLEUR RAPPORT QUALITÉ-PRIX',
          de_DE: 'BESTES ANGEBOT',
          es_ES: 'MEJOR VALOR',
          pt_PT: 'MELHOR OFERTA',
          pl_PL: 'NAJLEPSZA OFERTA',
          nl_NL: 'BESTE WAARDE',
          ko_KR: '최고의 가치',
          ja_JP: '最高のお得',
        },
        data: {},
        platformId: 'basic_bundle_5',
        iapCostCents: 9999,
        content: {
          [InventoryItem.Coin]: 130000,
          [InventoryItem.BoosterMove]: 9,
          [InventoryItem.BoosterRedraw]: 9,
          [InventoryItem.BoosterHammer]: 9,
          [InventoryItem.UnlimitedLivesMinutes]: 1200,
        },
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.SafetyNet,
    displayName: {
      en_US: 'Starter Bundle',
      it_IT: 'Pacchetto Iniziale',
      fr_FR: 'Pack de Démarrage',
      de_DE: 'Starter-Paket',
      es_ES: 'Paquete Inicial',
      pt_PT: 'Pacote Inicial',
      pl_PL: 'Pakiet Startowy',
      nl_NL: 'Starter Bundel',
      ko_KR: '스타터 번들',
      ja_JP: 'スターターバンドル',
    },
    location: [OfferLocation.Shop, OfferLocation.LevelFailBanner],
    triggers: [],
    startConditions: {
      groupType: 'AND',
      conditions: [
        {
          type: ConditionType.Bought,
          productId: IAPProductId.SafetyNet,
          operator: 'EQUAL',
          value: 0,
        },
      ],
    },
    endConditions: {
      groupType: 'AND',
      conditions: [
        {
          type: ConditionType.Bought,
          productId: IAPProductId.SafetyNet,
          operator: 'GREATER OR EQUAL',
          value: 1,
        },
      ],
    },

    data: null,
    timeLimit: {},
    tags: [OfferTag.SafetyNet],
    variants: [
      {
        id: 'Default',
        badge: {
          en_US: 'x6 Value',
          it_IT: 'Valore x6',
          fr_FR: 'Valeur x6',
          de_DE: 'Wert x6',
          es_ES: 'Valor x6',
          pt_PT: 'Valor x6',
          pl_PL: 'Wartość x6',
          nl_NL: 'Waarde x6',
          ko_KR: 'x6 가치',
          ja_JP: 'x6 バリュー',
        },
        iapCostCents: 499,
        content: {
          [InventoryItem.Coin]: 7000,
          [InventoryItem.BoosterMove]: 2,
          [InventoryItem.BoosterHammer]: 3,
          [InventoryItem.BoosterRedraw]: 8,
          [InventoryItem.UnlimitedLivesMinutes]: 120,
        },
        data: null,
        platformId: 'safety_net',
        conditions: null,
      },
    ],
    priority: 1000,
  },
  {
    id: IAPProductId.GoldenStartOffer,
    displayName: {
      en_US: 'Golden Head Start',
      it_IT: 'Partenza Dorata',
      fr_FR: 'Démarrage Doré',
      de_DE: 'Goldener Startvorteil',
      es_ES: 'Comienzo Dorado',
      pt_PT: 'Arranque Dourado',
      pl_PL: 'Złoty Start',
      nl_NL: 'Gouden Voorsprong',
      ko_KR: '골든 헤드 스타트',
      ja_JP: 'ゴールデン・ヘッドスタート',
    },
    location: [OfferLocation.MenuPopup, OfferLocation.MenuButton],
    triggers: [],
    data: {},
    startConditions: {
      groupType: 'AND',
      conditions: [
        {
          type: ConditionType.Bought,
          productId: IAPProductId.GoldenStartOffer,
          operator: 'LESS',
          value: 1,
        },
        {
          type: ConditionType.Shown,
          productId: IAPProductId.GoldenStartOffer,
          operator: 'LESS OR EQUAL',
          value: 1,
        },
        {
          type: ConditionType.ShownCooldownHours,
          productId: IAPProductId.GoldenStartOffer,
          passOnNotShown: true,
          operator: 'GREATER OR EQUAL',
          value: 48,
        },
        {
          type: ConditionType.Level,
          operator: 'GREATER OR EQUAL',
          value: 7,
        },
      ],
    },

    endConditions: {
      groupType: 'AND',
      conditions: [
        {
          type: ConditionType.BoughtInstanceLimit,
          operator: 'GREATER OR EQUAL',
          value: 1,
        },
      ],
    },
    timeLimit: { durationMinutes: 1440 },
    tags: ['SpecialOffer'],
    priority: 300,
    variants: [
      {
        id: 'Default',
        data: {
          popupLayout: {
            path: 'specialOffers/GoldenHeadStartPopupLayout',
          },
          menuIconAssets: {
            icon: { texture: 'Special_Offer_Icon' },
          },
        },
        badge: {
          en_US: '2x Value',
          it_IT: 'Valore 2x',
          fr_FR: 'Valeur 2x',
          de_DE: '2x Wert',
          es_ES: 'Valor 2x',
          pt_PT: 'Valor 2x',
          pl_PL: 'Wartość 2x',
          nl_NL: '2x Waarde',
          ko_KR: '2배 가치',
          ja_JP: '2倍の価値',
        },
        iapCostCents: 299,
        content: {
          [InventoryItem.Coin]: 2000,
          [InventoryItem.BoosterHammer]: 3,
          [InventoryItem.UnlimitedLivesMinutes]: 1440,
        },
        platformId: 'golden_offer',
        conditions: null,
      },
    ],
  },
  {
    id: IAPProductId.LegacyUpsellOffer,
    displayName: {
      en_US: 'Boost Bundle',
      it_IT: 'Pacchetto Potenziamento',
      fr_FR: 'Pack Boost',
      de_DE: 'Boost-Paket',
      es_ES: 'Paquete de Impulso',
      pt_PT: 'Pacote de Impulso',
      pl_PL: 'Pakiet Wzmocnień',
      nl_NL: 'Boost Bundel',
      ko_KR: '부스트 번들',
      ja_JP: 'ブーストバンドル',
    },
    location: [OfferLocation.LevelFailBanner, OfferLocation.Shop],
    triggers: [],
    data: null,
    startConditions: {
      groupType: 'OR',
      conditions: [
        {
          type: ConditionType.Trigger,
          trigger: OfferTrigger.NotEnoughCoins,
        },
        {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.InventoryBalance,
              inventoryType: InventoryItem.Coin,
              operator: 'LESS OR EQUAL',
              value: 700,
            },
            {
              type: ConditionType.Trigger,
              trigger: OfferTrigger.OpenShop,
            },
          ],
        },
      ],
    },

    endConditions: {
      groupType: 'AND',
      conditions: [
        {
          type: ConditionType.BoughtInstanceLimit,
          operator: 'GREATER OR EQUAL',
          value: 1,
        },
      ],
    },

    timeLimit: { durationMinutes: 48 * 60 },
    tags: [OfferTag.UpsellBundle],
    variants: [
      {
        id: 'Band_7',
        badge: null,
        iapCostCents: 9999,
        content: {
          [InventoryItem.Coin]: 126_000,
          [InventoryItem.UnlimitedLivesMinutes]: 1440,
          [InventoryItem.BoosterRedraw]: 8,
          [InventoryItem.BoosterHammer]: 8,
          [InventoryItem.BoosterMove]: 6,
        },
        data: null,
        platformId: 'upsell_offer_7',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.LegacyBand,
              operator: 'EQUAL',
              value: 7,
            },
          ],
        },
      },
      {
        id: 'Band_6',
        badge: null,
        iapCostCents: 6999,
        content: {
          [InventoryItem.Coin]: 84_000,
          [InventoryItem.UnlimitedLivesMinutes]: 1200,
          [InventoryItem.BoosterRedraw]: 7,
          [InventoryItem.BoosterHammer]: 7,
          [InventoryItem.BoosterMove]: 4,
        },
        data: null,
        platformId: 'upsell_offer_6',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.LegacyBand,
              operator: 'EQUAL',
              value: 6,
            },
          ],
        },
      },
      {
        id: 'Band_5',
        badge: null,
        iapCostCents: 4999,
        content: {
          [InventoryItem.Coin]: 56_000,
          [InventoryItem.UnlimitedLivesMinutes]: 960,
          [InventoryItem.BoosterRedraw]: 6,
          [InventoryItem.BoosterHammer]: 6,
          [InventoryItem.BoosterMove]: 4,
        },
        data: null,
        platformId: 'upsell_offer_5',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.LegacyBand,
              operator: 'EQUAL',
              value: 5,
            },
          ],
        },
      },
      {
        id: 'Band_4',
        badge: null,
        iapCostCents: 2999,
        content: {
          [InventoryItem.Coin]: 31_000,
          [InventoryItem.UnlimitedLivesMinutes]: 720,
          [InventoryItem.BoosterRedraw]: 5,
          [InventoryItem.BoosterHammer]: 5,
          [InventoryItem.BoosterMove]: 3,
        },
        data: null,
        platformId: 'upsell_offer_4',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.LegacyBand,
              operator: 'EQUAL',
              value: 4,
            },
          ],
        },
      },
      {
        id: 'Band_3',
        badge: null,
        iapCostCents: 1499,
        content: {
          [InventoryItem.Coin]: 15_000,
          [InventoryItem.UnlimitedLivesMinutes]: 360,
          [InventoryItem.BoosterRedraw]: 3,
          [InventoryItem.BoosterHammer]: 3,
          [InventoryItem.BoosterMove]: 2,
        },
        data: null,
        platformId: 'upsell_offer_3',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.LegacyBand,
              operator: 'EQUAL',
              value: 3,
            },
          ],
        },
      },
      {
        id: 'Band_2',
        badge: null,
        iapCostCents: 799,
        content: {
          [InventoryItem.Coin]: 8000,
          [InventoryItem.UnlimitedLivesMinutes]: 120,
          [InventoryItem.BoosterRedraw]: 2,
          [InventoryItem.BoosterHammer]: 2,
          [InventoryItem.BoosterMove]: 1,
        },
        data: null,
        platformId: 'upsell_offer_2',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.LegacyBand,
              operator: 'EQUAL',
              value: 2,
            },
          ],
        },
      },
      {
        id: 'Band_1',
        badge: null,
        iapCostCents: 499,
        content: {
          [InventoryItem.Coin]: 7000,
          [InventoryItem.UnlimitedLivesMinutes]: 120,
          [InventoryItem.BoosterRedraw]: 8,
          [InventoryItem.BoosterMove]: 2,
          [InventoryItem.BoosterHammer]: 3,
        },
        data: null,
        platformId: 'upsell_offer_1',
        conditions: null,
      },
    ],
    priority: 1000,
  },

  {
    id: IAPProductId.ShuffleUp,
    displayName: null,
    location: [OfferLocation.AdFallback],
    triggers: [],
    data: {},
    startConditions: null,
    endConditions: null,
    timeLimit: {
      durationMinutes: 1,
    },
    tags: ['AdFallback'],
    priority: 300,
    variants: [
      {
        id: 'NS',
        data: {
          popupLayout: {
            path: 'ads/SpecialOfferAdFallbackLayout',
            skin: 'fallback_shuffle_ns',
          },
        },
        badge: {},
        iapCostCents: 99,
        content: {
          [InventoryItem.BoosterRedraw]: 3,
        },
        platformId: 'fallback_shuffle_ns',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.Payer,
              value: false,
            },
          ],
        },
      },
      {
        id: 'Band1',
        data: {
          popupLayout: {
            path: 'ads/SpecialOfferAdFallbackLayout',
            skin: 'fallback_shuffle_1',
          },
        },
        badge: {},
        iapCostCents: 299,
        content: {
          [InventoryItem.BoosterRedraw]: 3,
          [InventoryItem.Coin]: 1000,
        },
        platformId: 'fallback_shuffle_1',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.Band,
              operator: 'EQUAL',
              value: 1,
            },
          ],
        },
      },
      {
        id: 'Band2',
        data: {
          popupLayout: {
            path: 'ads/SpecialOfferAdFallbackLayout',
            skin: 'fallback_shuffle_2',
          },
        },
        badge: {},
        iapCostCents: 499,
        content: {
          [InventoryItem.BoosterRedraw]: 4,
          [InventoryItem.Coin]: 2000,
        },
        platformId: 'fallback_shuffle_2',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.Band,
              operator: 'EQUAL',
              value: 2,
            },
          ],
        },
      },
      {
        id: 'Band3',
        data: {
          popupLayout: {
            path: 'ads/SpecialOfferAdFallbackLayout',
            skin: 'fallback_shuffle_3',
          },
        },
        badge: {},
        iapCostCents: 799,
        content: {
          [InventoryItem.BoosterRedraw]: 6,
          [InventoryItem.Coin]: 3000,
        },
        platformId: 'fallback_shuffle_3',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.Band,
              operator: 'GREATER OR EQUAL',
              value: 3,
            },
          ],
        },
      },
    ],
  },

  {
    id: IAPProductId.HammerTime,
    displayName: null,
    location: [OfferLocation.AdFallback],
    triggers: [],
    data: {},
    startConditions: null,
    endConditions: null,
    timeLimit: {
      durationMinutes: 1,
    },
    tags: ['AdFallback'],
    priority: 300,
    variants: [
      {
        id: 'NS',
        data: {
          popupLayout: {
            path: 'ads/SpecialOfferAdFallbackLayout',
            skin: 'fallback_hammer_ns',
          },
        },
        badge: {},
        iapCostCents: 199,
        content: {
          [InventoryItem.BoosterHammer]: 2,
        },
        platformId: 'fallback_hammer_ns',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.Payer,
              value: false,
            },
          ],
        },
      },
      {
        id: 'Band1',
        data: {
          popupLayout: {
            path: 'ads/SpecialOfferAdFallbackLayout',
            skin: 'fallback_hammer_1',
          },
        },
        badge: {},
        iapCostCents: 299,
        content: {
          [InventoryItem.BoosterHammer]: 2,
        },
        platformId: 'fallback_hammer_1',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.Band,
              operator: 'EQUAL',
              value: 1,
            },
          ],
        },
      },
      {
        id: 'Band2',
        data: {
          popupLayout: {
            path: 'ads/SpecialOfferAdFallbackLayout',
            skin: 'fallback_hammer_2',
          },
        },
        badge: {},
        iapCostCents: 499,
        content: {
          [InventoryItem.BoosterHammer]: 3,
        },
        platformId: 'fallback_hammer_2',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.Band,
              operator: 'EQUAL',
              value: 2,
            },
          ],
        },
      },
      {
        id: 'Band3',
        data: {
          popupLayout: {
            path: 'ads/SpecialOfferAdFallbackLayout',
            skin: 'fallback_hammer_3',
          },
        },
        badge: {},
        iapCostCents: 799,
        content: {
          [InventoryItem.BoosterHammer]: 4,
        },
        platformId: 'fallback_hammer_3',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.Band,
              operator: 'GREATER OR EQUAL',
              value: 3,
            },
          ],
        },
      },
    ],
  },

  {
    id: IAPProductId.Lifeline,
    displayName: null,
    location: [OfferLocation.AdFallback],
    triggers: [],
    data: {},
    startConditions: null,
    endConditions: null,
    timeLimit: {
      durationMinutes: 1,
    },
    tags: ['AdFallback'],
    priority: 300,
    variants: [
      {
        id: 'Band1',
        data: {
          popupLayout: {
            path: 'ads/SpecialOfferAdFallbackLayout',
            skin: 'fallback_lifeline_1',
          },
        },
        badge: {},
        iapCostCents: 299,
        content: {
          [InventoryItem.UnlimitedLivesMinutes]: 90,
          [InventoryItem.Coin]: 1000,
          [InventoryItem.FreeSpin]: 2,
        },
        platformId: 'fallback_lifeline_1',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.Band,
              operator: 'EQUAL',
              value: 1,
            },
          ],
        },
      },
      {
        id: 'Band2',
        data: {
          popupLayout: {
            path: 'ads/SpecialOfferAdFallbackLayout',
            skin: 'fallback_lifeline_2',
          },
        },
        badge: {},
        iapCostCents: 499,
        content: {
          [InventoryItem.UnlimitedLivesMinutes]: 120,
          [InventoryItem.Coin]: 2000,
          [InventoryItem.FreeSpin]: 4,
        },
        platformId: 'fallback_lifeline_2',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.Band,
              operator: 'EQUAL',
              value: 2,
            },
          ],
        },
      },
      {
        id: 'Band3',
        data: {
          popupLayout: {
            path: 'ads/SpecialOfferAdFallbackLayout',
            skin: 'fallback_lifeline_3',
          },
        },
        badge: {},
        iapCostCents: 799,
        content: {
          [InventoryItem.UnlimitedLivesMinutes]: 180,
          [InventoryItem.Coin]: 3000,
          [InventoryItem.FreeSpin]: 6,
        },
        platformId: 'fallback_lifeline_3',
        conditions: {
          groupType: 'AND',
          conditions: [
            {
              type: ConditionType.Band,
              operator: 'GREATER OR EQUAL',
              value: 3,
            },
          ],
        },
      },
    ],
  },

  {
    id: IAPProductId.SpinFrenzy,
    displayName: null,
    location: [OfferLocation.AdFallback],
    triggers: [],
    data: {},
    startConditions: {
      groupType: 'AND',
      conditions: [
        {
          type: ConditionType.Payer,
          value: false,
        },
      ],
    },
    endConditions: null,
    timeLimit: {
      durationMinutes: 1,
    },
    tags: ['AdFallback'],
    priority: 300,
    variants: [
      {
        id: 'Default',
        data: {
          popupLayout: {
            path: 'ads/SpecialOfferAdFallbackLayout',
            skin: 'fallback_spin_ns',
          },
        },
        badge: {},
        iapCostCents: 99,
        content: {
          [InventoryItem.FreeSpin]: 10,
        },
        platformId: 'fallback_spin_ns',
        conditions: null,
      },
    ],
  },
];
